import React, { useEffect, useState, useCallback } from "react";
import { FaTrashAlt, FaShoppingBag, FaMapMarkerAlt, FaStickyNote, FaGift, FaUserFriends } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { useCart } from "../context/cartContext";
import { showSuccessNotification, showErrorNotification, showWarningNotification, showInfoNotification } from "../components/NotificationManager";
import UserLookup from "../components/UserLookup";
import QuickReorderManager from "../components/QuickReorderManager";
import ReorderSystem from "../components/ReorderSystem";
import OrderTypeDropdown from "../components/OrderTypeDropdown";
import LoadingPage from "../components/Loader";
import EnhancedPaymentModal from "../components/EnhancedPaymentModal";
import axios from "axios";

const CartPage = () => {
  const { cart, removeItem, clearCart, updateQuantity } = useCart();
  const [user, setUser] = useState(null);
  const [note, setNote] = useState('');
  const [fee, setFee] = useState(0);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeError, setFeeError] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentCheckoutData, setCurrentCheckoutData] = useState(null);
  const [userBalance, setUserBalance] = useState(0);
  const [isOrderLoading, setIsOrderLoading] = useState(false);
  
  const [isOrderingForOther, setIsOrderingForOther] = useState(false);
  const [recipientInfo, setRecipientInfo] = useState(null);
  const [orderForUsername, setOrderForUsername] = useState('');
  const [overrideDeliveryInfo, setOverrideDeliveryInfo] = useState({
    location: '',
    phoneNumber: '',
    nearestLandmark: ''
  });
  
  const [isOrderingByReference, setIsOrderingByReference] = useState(false);
  const [orderCustomId, setOrderCustomId] = useState('');
  
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const [isOrderingForExternal, setIsOrderingForExternal] = useState(false);
  const [externalRecipient, setExternalRecipient] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    nearestLandmark: '',
  });
  
  const navigate = useNavigate();
  
  // Preview reorder function
  const previewReorder = async () => {
    if (!orderCustomId.trim()) {
      showWarningNotification('Please enter an order ID to preview');
      return;
    }

    setIsPreviewLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        'https://mongobyte.vercel.app/api/v1/orders/preview-reorder',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            orderCustomId: orderCustomId.trim()
          })
        }
      );
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to preview order');
      }
      
      if (result.success && result.preview) {
        setPreviewData(result.preview);
        setShowPreview(true);
        
        // Show any issues
        if (!result.preview.canProceed.status) {
          showWarningNotification(
            `Issues found: ${result.preview.canProceed.issues.join(', ')}`
          );
        } else {
          showSuccessNotification('Order preview loaded successfully!');
        }
      } else {
        throw new Error(result.message || 'Failed to generate preview');
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      showErrorNotification(error.message || 'Unable to generate preview');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const refreshUserProfile = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    
    try {
      const response = await axios.get(
        "https://mongobyte.vercel.app/api/v1/users/getProfile",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      const freshUserData = response.data.user;
      setUser(freshUserData);
      localStorage.setItem("byteUser", JSON.stringify(freshUserData));
      setUserBalance(freshUserData.byteBalance || 0);
      // console.log("Profile refreshed with latest data");
      return freshUserData;
    } catch (error) {
      console.error("Error refreshing user profile:", error);
      return null;
    }
  }, []);

  const fetchUserBalance = useCallback(async (username) => {
    if (!username) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://mongobyte.vercel.app/api/v1/users/balance/${username}`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserBalance(data.balance || 0);
      }
    } catch (error) {
      console.error("Error fetching user balance:", error);
    }
  }, []);

  const fetchDeliveryFee = useCallback(async (restaurantId) => {
    setFeeLoading(true);
    setFeeError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://mongobyte.vercel.app/api/v1/delivery-fees/check-fee/${restaurantId}`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch delivery fee');
      }
      
      setFee(data.deliveryFee);
      setFeeError(null);
    } catch (error) {
      console.error('Error fetching delivery fee:', error);
      setFeeError(error.message);
      setFee(0);
    } finally {
      setFeeLoading(false);
    }
  }, []);

  useEffect(() => {
    if (cart.size > 0) {
      const [[restaurantId]] = cart.entries();
      if (restaurantId && user?.nearestLandmark) {
        fetchDeliveryFee(restaurantId);
      }
    }
  }, [cart, user?.nearestLandmark, fetchDeliveryFee]);

  useEffect(() => {
    const handleProfileUpdate = (event) => {
      const updatedUser = event.detail;
      // console.log("Profile updated, refreshing user data:", updatedUser);
      setUser(prevUser => ({
        ...prevUser,
        ...updatedUser
      }));
    };

    window.addEventListener('userProfileUpdated', handleProfileUpdate);
    return () => {
      window.removeEventListener('userProfileUpdated', handleProfileUpdate);
    };
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      try {
        const storedUser = jwtDecode(storedToken);
        setUser(storedUser);
        
        (async () => {
          const freshUserData = await refreshUserProfile();
          if (freshUserData) {
            await fetchUserBalance(freshUserData.username);
            // showSuccessNotification("Profile data refreshed successfully!");
          } else {
            const byteUser = localStorage.getItem("byteUser");
            if (byteUser) {
              try {
                const parsedUser = JSON.parse(byteUser);
                setUser(prevUser => ({
                  ...prevUser,
                  ...parsedUser
                }));
                await fetchUserBalance(parsedUser.username || storedUser.user.username);
              } catch (error) {
                console.error("Error parsing byteUser from localStorage:", error);
              }
            }
            showInfoNotification("Using cached profile data");
          }
        })();
      } catch (error) {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate, fetchUserBalance, refreshUserProfile]);

  const handleRemoveItem = useCallback((restaurantId, mealId) => {
    removeItem(mealId);
    showSuccessNotification("Item removed from cart!");
  }, [removeItem]);

  const handleClearCart = useCallback((restaurantId) => {
    clearCart();
    showSuccessNotification("Cart cleared successfully!");
  }, [clearCart]);

  const totalAmountPerRestaurant = useCallback((items = [], fee = 0) => {
    if (!Array.isArray(items)) return parseFloat(fee || 0);
    
    return items
      .filter(item => item && item.meal && typeof item.quantity === 'number' && typeof item.meal.price === 'number')
      .reduce(
        (sum, item) => sum + (item.meal.price * item.quantity),
        0
      ) + parseFloat(fee || 0);
  }, []);

  const isOnlyAddOns = (items) => {
    return Array.isArray(items) && 
           items.length > 0 && 
           items.every(item => 
             item.meal && 
             item.meal.tag === 'add-on'
           );
  };

  const handleUserFound = (userInfo) => {
    setRecipientInfo(userInfo);
    setOrderForUsername(userInfo.username);
    
    if (userInfo.hasDeliveryInfo) {
      setOverrideDeliveryInfo({
        location: userInfo.location || '',
        phoneNumber: userInfo.phoneNumber || '',
        nearestLandmark: userInfo.nearestLandmark || ''
      });
    } else {
      setOverrideDeliveryInfo({
        location: '',
        phoneNumber: '',
        nearestLandmark: ''
      });
    }
  };

  const handleUserNotFound = (error) => {
    setRecipientInfo(null);
    setOrderForUsername('');
    setOverrideDeliveryInfo({
      location: '',
      phoneNumber: '',
      nearestLandmark: ''
    });
  };

  const handleClearUserSelection = () => {
    setRecipientInfo(null);
    setOrderForUsername('');
    setOverrideDeliveryInfo({
      location: '',
      phoneNumber: '',
      nearestLandmark: ''
    });
  };

  const toggleOrderType = (type) => {
    setIsOrderingForOther(type === 'other');
    setIsOrderingForExternal(type === 'external');
    setIsOrderingByReference(type === 'reference');
    if (type !== 'other') handleClearUserSelection();
    if (type !== 'reference') setOrderCustomId('');
    if (type !== 'external') {
      setExternalRecipient({ name: '', phone: '', email: '', location: '', nearestLandmark: '' });
    }
  };

  const handleCheckout = useCallback(async (restaurantId = null) => {
    setIsCheckoutLoading(true); // Set loading state immediately for instant feedback
    try {
      if (!user) {
        showErrorNotification("You need to log in first.");
        return;
      }

      const freshUserData = await refreshUserProfile();
      let finalUserData = freshUserData || user;

      if (isOrderingByReference) {
        if (!orderCustomId.trim()) {
          showWarningNotification("Please provide an order ID to create a new order from.");
          return;
        }

        setIsOrderLoading(true);
        try {
          const token = localStorage.getItem('token');
          const cartItemsForOverride = restaurantId ? cart.get(restaurantId) || [] : [];
          const requestBody = {
            orderCustomId: orderCustomId.trim(),
          };

          if (note.trim()) {
            requestBody.note = note;
          }

          if (cartItemsForOverride.length > 0) {
            requestBody.mealsOverride = cartItemsForOverride.map(({ meal, quantity }) => ({
              mealId: meal.customId,
              quantity,
            }));
          }

          const response = await fetch("https://mongobyte.vercel.app/api/v1/orders/by-reference", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(requestBody)
          });

          const data = await response.json();
          
          if (!response.ok) {
            if (data.unavailableMeals && data.unavailableMeals.length > 0) {
              const unavailableList = data.unavailableMeals
                .map(meal => `• ${meal.name} (${meal.reason})`)
                .join('\n');
              showErrorNotification(`Some meals are no longer available:\n${unavailableList}\nPlease try with a different order or contact the restaurant.`);
              return;
            }
            
            if (data.missingFields) {
              const missing = data.missingFields.join(" and ");
              showWarningNotification(`Please update your profile with: ${missing}. Go to profile settings and add the missing information.`);
              return;
            }
            
            if (data.shortfall) {
              showErrorNotification(`You need ₦${data.shortfall} more to place this order. Please fund your wallet and try again.`);
              return;
            }
            
            if (data.message && data.message.includes('not found')) {
              showErrorNotification(`Order Not Found: ${data.message}. Please check the order ID and try again.`);
              return;
            }
            
            throw new Error(data.message || "Failed to create order from previous order");
          }

          clearCart();
          setNote('');
          setOrderCustomId('');
          showSuccessNotification(`Order Placed Successfully! 🎉 New Order ID: ${data.order?.customId || "Order"}`);
          setTimeout(() => setIsOrderLoading(false), 2000);
          return;
        } catch (error) {
          console.error("Error creating order from previous order:", error);
          showErrorNotification(error.message || "Failed to create order from previous order");
          return;
        } finally {
          setIsOrderLoading(false);
        }
      }

      if (!restaurantId) {
        showWarningNotification("No restaurant selected. Please add items to your cart or specify a restaurant.");
        return;
      }

      const itemsForRestaurant = cart.get(restaurantId) || [];
      if (itemsForRestaurant.length === 0) {
        showWarningNotification("No items to checkout.");
        return;
      }
      if (isOnlyAddOns(itemsForRestaurant)) {
        showWarningNotification("You cannot place an order with only add-ons in your cart. Please add a main meal.");
        return;
      }

      const totalAmount = totalAmountPerRestaurant(itemsForRestaurant, fee);

      if (isOrderingForExternal) {
        if (!externalRecipient.name.trim() || !externalRecipient.phone.trim() || !externalRecipient.location.trim()) {
          showWarningNotification("Please provide recipient's name, phone, and delivery location.");
          return;
        }
      } else if (isOrderingForOther) {
        if (!recipientInfo || !orderForUsername) {
          showWarningNotification("Please select a recipient for this gift order.");
          return;
        }
        const finalLocation = overrideDeliveryInfo.location || recipientInfo.location;
        const finalPhone = overrideDeliveryInfo.phoneNumber || recipientInfo.phoneNumber;
        if (!finalLocation || !finalPhone) {
          showWarningNotification("Please provide delivery location and phone number for the recipient.");
          return;
        }
      } else {
        if (!finalUserData.location || finalUserData.location === "" || 
            !finalUserData.nearestLandmark || finalUserData.nearestLandmark === "") {
          showWarningNotification("Complete profile setup to proceed with the order.");
          navigate('/user/profile');
          return;
        }
      }

      const orderDetails = {
        restaurantCustomId: restaurantId,
        meals: itemsForRestaurant.map(({ meal, quantity }) => ({
          mealId: meal.customId,
          quantity,
        })),
        totalPrice: totalAmount,
        note: isOrderingForOther 
          ? `Gift order for @${orderForUsername}. ${note}`.trim()
          : note,
        fee: parseFloat(fee) || 1000,
        location: "",
        phoneNumber: "",
        nearestLandmark: ""
      };

      if (isOrderingForExternal) {
        orderDetails.recipientName = externalRecipient.name;
        orderDetails.recipientPhone = externalRecipient.phone;
        orderDetails.recipientEmail = externalRecipient.email;
        orderDetails.location = externalRecipient.location;
        orderDetails.nearestLandmark = externalRecipient.nearestLandmark;
      } else if (isOrderingForOther) {
        orderDetails.orderForUsername = orderForUsername;
        orderDetails.location = overrideDeliveryInfo.location || recipientInfo.location;
        orderDetails.phoneNumber = overrideDeliveryInfo.phoneNumber || recipientInfo.phoneNumber;
        orderDetails.nearestLandmark = overrideDeliveryInfo.nearestLandmark || recipientInfo.nearestLandmark || "";
      } else {
        orderDetails.location = finalUserData.location;
        orderDetails.phoneNumber = finalUserData.phoneNumber;
        orderDetails.nearestLandmark = finalUserData.nearestLandmark || "";
      }

      if (!orderDetails.location || !orderDetails.phoneNumber) {
        showWarningNotification("Missing delivery information. Please ensure location and phone number are provided.");
        return;
      }

      await fetchUserBalance(finalUserData.username);

      setCurrentCheckoutData({
        restaurantId,
        itemsForRestaurant,
        totalAmount,
        orderDetails,
        isExternal: isOrderingForExternal,
      });
      setShowPaymentModal(true);
    } catch (error) {
      console.error("Checkout error:", error);
      showErrorNotification("An error occurred while preparing checkout. Please try again.");
    } finally {
      setIsCheckoutLoading(false); // Reset loading state
    }
  }, [cart, fee, note, totalAmountPerRestaurant, user, isOrderingForOther, recipientInfo, orderForUsername, overrideDeliveryInfo, fetchUserBalance, navigate, refreshUserProfile, isOrderingForExternal, externalRecipient, clearCart, isOrderingByReference, orderCustomId]);

  const processWalletPayment = useCallback(async () => {
    if (!currentCheckoutData) return;

    const { totalAmount, orderDetails, isExternal } = currentCheckoutData;

    if (!orderDetails) {
      showErrorNotification("Order details are missing. Please try again.");
      setIsCheckoutLoading(false);
      setIsOrderLoading(false);
      return;
    }

    await fetchUserBalance(user.username);
    if (userBalance < totalAmount) {
      showErrorNotification("Insufficient Balance. Please add funds to your wallet or pay with card!");
      return;
    }

    setIsCheckoutLoading(true);
    setIsOrderLoading(true);

    try {
      const token = localStorage.getItem('token');
      let response, responseData;
      if (isExternal) {
        response = await fetch("https://mongobyte.vercel.app/api/v1/orders/gift-external", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            recipientName: orderDetails.recipientName,
            recipientPhone: orderDetails.recipientPhone,
            recipientEmail: orderDetails.recipientEmail,
            location: orderDetails.location,
            nearestLandmark: orderDetails.nearestLandmark,
            meals: orderDetails.meals,
            note: orderDetails.note,
            totalPrice: orderDetails.totalPrice,
            fee: orderDetails.fee,
            restaurantCustomId: orderDetails.restaurantCustomId,
            paymentMethod: 'wallet',
          }),
        });
      } else {
        response = await fetch("https://mongobyte.vercel.app/api/v1/orders/create", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            meals: orderDetails.meals,
            note: orderDetails.note,
            totalPrice: orderDetails.totalPrice,
            location: orderDetails.location,
            phoneNumber: orderDetails.phoneNumber,
            restaurantCustomId: orderDetails.restaurantCustomId,
            nearestLandmark: orderDetails.nearestLandmark,
            fee: orderDetails.fee,
            ...(orderDetails.orderForUsername && { orderForUsername: orderDetails.orderForUsername }),
            paymentMethod: 'wallet'
          }),
        });
      }

      responseData = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          showErrorNotification("Session expired. Please log in again.");
          navigate('/login');
          return;
        }
        throw new Error(responseData.message || "Failed to place the order.");
      }

      clearCart();
      setNote('');
      setShowPaymentModal(false);
      setCurrentCheckoutData(null);
      showSuccessNotification("Order Placed Successfully! Your order has been confirmed and is being prepared.");
      setTimeout(() => setIsOrderLoading(false), 2000);
    } catch (error) {
      console.error("Wallet payment error:", error);
      showErrorNotification(error.message || "Something went wrong.");
    } finally {
      setIsCheckoutLoading(false);
      setIsOrderLoading(false);
    }
  }, [currentCheckoutData, clearCart, setNote, userBalance, user, fetchUserBalance, navigate]);

  if (!user) return null;

  const isProfileIncomplete = !user.location || user.location === "" || 
                             !user.nearestLandmark || user.nearestLandmark === "";

  const firstRestaurantId = cart.size > 0 ? Array.from(cart.entries())[0][0] : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-6 px-4 pt-16 md:pt-24 pb-24 md:pb-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <h1 className="text-3xl md:text-4xl font-bold text-crust flex items-center gap-3">
              <FaShoppingBag className="text-pepperoni" />
              Your Cart
            </h1>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/user/orderhistory')}
              className="bg-gradient-to-r from-pepperoni to-primary-900 hover:from-primary-900 hover:to-pepperoni text-white px-6 py-2 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              title="View Order History"
            >
              Order History
            </motion.button>
          </div>
          <p className="text-gray-600 font-sans">Review your delicious selections</p>
        </motion.div>

        {!isOrderingForOther && isProfileIncomplete && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-lg shadow-md"
          >
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Your profile is incomplete. Please 
                  <button 
                    onClick={() => navigate('/user/profile')} 
                    className="font-medium underline text-yellow-800 hover:text-yellow-900 ml-1"
                  >
                    complete your profile
                  </button> 
                  to place an order.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6"
        >
          <OrderTypeDropdown 
            selectedType={
              isOrderingForOther ? 'other' : 
              isOrderingForExternal ? 'external' : 
              isOrderingByReference ? 'reference' : 'self'
            }
            onTypeChange={toggleOrderType}
            isOrderingForOther={isOrderingForOther}
            isOrderingForExternal={isOrderingForExternal}
            isOrderingByReference={isOrderingByReference}
          />

          {isOrderingByReference && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-6 border-2 border-yellow-200 mt-4"
            >
              <h4 className="text-lg font-bold text-crust mb-4 flex items-center gap-2">
                <FaStickyNote className="text-yellow-600" />
                Reorder a Previous Order
              </h4>
              <div>
                <input
                  type="text"
                  value={orderCustomId}
                  onChange={(e) => setOrderCustomId(e.target.value)}
                  placeholder="Enter the order ID you want to reorder"
                  className="w-full p-3 border border-yellow-300 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent font-sans"
                  required
                />
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <p className="mb-2">💡 Pro Tips:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Your current profile information will be used automatically</li>
                  <li>Only add items to cart if you want to modify the original order</li>
                  <li>Unavailable items will be reported before placing the order</li>
                  <li>Make sure your profile has complete delivery information</li>
                </ul>
              </div>
              <div className="mt-4 space-y-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={previewReorder}
                  disabled={!orderCustomId.trim() || isPreviewLoading}
                  className={`w-full py-3 px-4 rounded-xl font-bold text-lg shadow-lg transition-all duration-300 ${
                    !orderCustomId.trim() || isPreviewLoading
                      ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                      : "bg-gradient-to-r from-secondary-500 to-primary-600 hover:from-primary-600 hover:to-secondary-500 text-white"
                  }`}
                >
                  {isPreviewLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      Loading Preview...
                    </div>
                  ) : (
                    'Preview Order'
                  )}
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleCheckout(firstRestaurantId)}
                  disabled={!orderCustomId.trim() || (!firstRestaurantId && cart.size > 0)}
                  className={`w-full py-3 px-4 rounded-xl font-bold text-lg shadow-lg transition-all duration-300 ${
                    !orderCustomId.trim() || (!firstRestaurantId && cart.size > 0)
                      ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                      : "bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-amber-500 hover:to-yellow-500 text-white"
                  }`}
                >
                  Reorder Now
                </motion.button>
              </div>
            </motion.div>
          )}

          {isOrderingForOther && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <UserLookup
                onUserFound={handleUserFound}
                onUserNotFound={handleUserNotFound}
                onClear={handleClearUserSelection}
              />
            </motion.div>
          )}

          {isOrderingForExternal && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 border-2 border-green-200 mt-4"
            >
              <h4 className="text-lg font-bold text-crust mb-4 flex items-center gap-2">
                <FaUserFriends className="text-green-600" />
                External Recipient Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Recipient Name *
                  </label>
                  <input
                    type="text"
                    value={externalRecipient.name}
                    onChange={e => setExternalRecipient(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter recipient's full name"
                    className="w-full p-3 border border-green-300 rounded-xl focus:ring-2 focus:ring-green-400 focus:border-transparent font-sans"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Recipient Phone *
                  </label>
                  <input
                    type="tel"
                    value={externalRecipient.phone}
                    onChange={e => setExternalRecipient(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Enter recipient's phone number"
                    className="w-full p-3 border border-green-300 rounded-xl focus:ring-2 focus:ring-green-400 focus:border-transparent font-sans"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Delivery Location *
                  </label>
                  <input
                    type="text"
                    value={externalRecipient.location}
                    onChange={e => setExternalRecipient(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="Enter delivery location"
                    className="w-full p-3 border border-green-300 rounded-xl focus:ring-2 focus:ring-green-400 focus:border-transparent font-sans"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nearest Landmark
                  </label>
                  <input
                    type="text"
                    value={externalRecipient.nearestLandmark}
                    onChange={e => setExternalRecipient(prev => ({ ...prev, nearestLandmark: e.target.value }))}
                    placeholder="Enter nearest landmark (optional)"
                    className="w-full p-3 border border-green-300 rounded-xl focus:ring-2 focus:ring-green-400 focus:border-transparent font-sans"
                  />
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {cart.size === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
          >
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-600 mb-2">Your cart is empty</h2>
              <p className="text-gray-500 mb-4">Add meals to your cart or use a previous order ID to continue</p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/user')}
              className="bg-pepperoni text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Browse Restaurants
            </motion.button>
          </motion.div>
        ) : (
          <div className="space-y-6">
            {Array.from(cart.entries()).map(([restaurantId, items], index) => (
              <motion.div
                key={restaurantId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-crust to-crust/90 text-white p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-bold font-secondary">
                        {items.length > 0 ? (items[0].meal.restaurantName || items[0].restaurantName || "Unknown Restaurant") : "Unknown Restaurant"}
                      </h2>
                      <p className="text-cheese/80 text-sm mt-1">
                        {items.length} item{items.length !== 1 ? 's' : ''} in cart
                      </p>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleClearCart(restaurantId)}
                      className="bg-red-500 hover:bg-red-600 text-white p-3 rounded-xl shadow-lg transition-all duration-300"
                    >
                      <FaTrashAlt />
                    </motion.button>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <AnimatePresence>
                    {Array.isArray(items) && items
                      .filter(item => item && item.meal && item.quantity)
                      .map(({ meal, quantity }) => (
                        meal && meal.customId ? (
                          <motion.div
                            key={meal.customId}
                            layout
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            className="p-4 bg-gray-50 rounded-xl border border-gray-100 hover:shadow-md transition-all duration-300 mb-4"
                          >
                            <div className="flex items-center gap-4">
                              {meal.imageUrl && (
                                <div className="relative w-20 h-20 rounded-xl overflow-hidden shadow-md">
                                  <img 
                                    src={meal.imageUrl} 
                                    alt={meal.name} 
                                    className="w-full h-full object-cover" 
                                  />
                                </div>
                              )}
                              <div className="flex-1">
                                <h3 className="text-lg font-bold text-crust">
                                  {meal.name}
                                </h3>
                                <p className="text-xl font-bold text-pepperoni">₦{(meal.price * quantity).toFixed(2)}</p>
                                <p className="text-sm text-gray-500">₦{meal.price.toFixed(2)} each</p>
                              </div>
                              {/* Quantity Controls */}
                              <div className="flex items-center gap-3">
                                <div className="flex items-center bg-white border border-gray-200 rounded-xl shadow-sm">
                                  <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => updateQuantity(meal.customId, Math.max(1, quantity - 1))}
                                    disabled={quantity <= 1}
                                    className={`p-2 rounded-l-xl transition-all duration-200 ${
                                      quantity <= 1 
                                        ? 'text-gray-300 cursor-not-allowed' 
                                        : 'text-gray-600 hover:text-pepperoni hover:bg-gray-50'
                                    }`}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                    </svg>
                                  </motion.button>
                                  <div className="px-4 py-2 min-w-[60px] text-center">
                                    <span className="font-semibold text-gray-800">{quantity}</span>
                                  </div>
                                  <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => updateQuantity(meal.customId, quantity + 1)}
                                    className="p-2 rounded-r-xl text-gray-600 hover:text-pepperoni hover:bg-gray-50 transition-all duration-200"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                  </motion.button>
                                </div>
                              </div>
                            </div>
                            {/* Remove button below item */}
                            <div className="flex mt-4">
                              <motion.button
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                                onClick={() => handleRemoveItem(restaurantId, meal.customId)}
                                className="w-full bg-red-100 hover:bg-red-200 text-red-600 p-3 rounded-xl transition-all duration-300 flex items-center justify-center"
                                title="Remove item"
                              >
                                <FaTrashAlt className="mx-auto" />
                              </motion.button>
                            </div>
                          </motion.div>
                        ) : null
                      ))}
                  </AnimatePresence>
                </div>

                <div className="px-6 pb-6 space-y-4">
                  <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                    <label className="flex items-center gap-2 text-sm font-semibold text-crust mb-3">
                      <FaStickyNote className="text-yellow-600" />
                      {isOrderingForOther ? `Add a note for ${recipientInfo?.username || 'recipient'}'s order` : 'Add a note for your order'}
                    </label>
                    <textarea
                      className="w-full p-3 border border-yellow-300 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent resize-none font-sans"
                      rows="3"
                      placeholder={
                        isOrderingForOther 
                          ? `Special message or delivery instructions for ${recipientInfo?.username || 'recipient'}...`
                          : "Special requests, allergies, or preferences..."
                      }
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                  </div>

                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <label className="flex items-center gap-2 text-sm font-semibold text-crust">
                        <FaMapMarkerAlt className="text-blue-600" />
                        Delivery Fee
                      </label>
                      {feeLoading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                      )}
                    </div>
                    {feeError ? (
                      <div className="bg-red-50 rounded-lg p-3 mb-2">
                        <p className="text-sm text-red-600">{feeError}</p>
                        {feeError.includes('landmark') && (
                          <button
                            onClick={() => navigate('/user/profile')}
                            className="text-sm text-red-700 underline hover:text-red-800 mt-1"
                          >
                            Update Profile
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Delivery to:</span>
                            <span className="font-semibold text-gray-800">{user?.nearestLandmark || 'Not set'}</span>
                          </div>
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-gray-600">Fee:</span>
                            <span className="font-bold text-lg text-blue-600">₦{fee.toFixed(2)}</span>
                          </div>
                        </div>
                        {!user?.nearestLandmark && (
                          <button
                            onClick={() => navigate('/user/profile')}
                            className="text-sm text-blue-600 underline hover:text-blue-700"
                          >
                            Set your landmark in profile
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="bg-gradient-to-r from-pepperoni/10 to-cheese/10 rounded-xl p-4 border border-pepperoni/20">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-lg font-semibold text-crust">Subtotal:</span>
                      <span className="text-lg font-bold text-crust">
                        ₦{items.reduce((sum, item) => sum + (item.meal?.price ?? 0) * (item.quantity ?? 0), 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-lg font-semibold text-crust">Delivery Fee:</span>
                      <span className="text-lg font-bold text-crust">₦{parseFloat(fee || 0).toFixed(2)}</span>
                    </div>
                    <hr className="border-pepperoni/20 mb-4" />
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold text-crust">Total:</span>
                      <span className="text-2xl font-bold text-pepperoni">
                        ₦{totalAmountPerRestaurant(items, fee).toFixed(2)}
                      </span>
                    </div>
                    {isOrderingForOther && recipientInfo && (
                      <div className="mt-4 pt-4 border-t border-pepperoni/20">
                        <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-3">
                          <h4 className="font-bold text-purple-700 mb-2 flex items-center gap-2">
                            <FaGift />
                            Gift Order Summary
                          </h4>
                          <div className="text-sm space-y-1">
                            <p><span className="font-semibold">Recipient:</span> @{recipientInfo.username}</p>
                            <p><span className="font-semibold">Delivery to:</span> {overrideDeliveryInfo.location || recipientInfo.location}</p>
                            <p><span className="font-semibold">Contact:</span> {overrideDeliveryInfo.phoneNumber || recipientInfo.phoneNumber}</p>
                            {(overrideDeliveryInfo.nearestLandmark || recipientInfo.nearestLandmark) && (
                              <p><span className="font-semibold">Landmark:</span> {overrideDeliveryInfo.nearestLandmark || recipientInfo.nearestLandmark}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <QuickReorderManager
                      userToken={localStorage.getItem('token')}
                      cartItems={items}
                      restaurant={restaurantId}
                      onSaveSuccess={(savedOrder) => {
                        // console.log('Quick reorder saved:', savedOrder);
                      }}
                    />
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleCheckout(restaurantId)}
                    disabled={isCheckoutLoading || isOnlyAddOns(items)}
                    className={`w-full py-4 px-6 rounded-xl font-bold text-lg shadow-lg transition-all duration-300 ${
                      isCheckoutLoading || isOnlyAddOns(items)
                        ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                        : "bg-gradient-to-r from-pepperoni to-red-600 hover:from-red-600 hover:to-pepperoni text-white shadow-xl hover:shadow-2xl"
                    }`}
                  >
                    {isCheckoutLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                        Preparing Order...
                      </div>
                    ) : isOnlyAddOns(items) ? (
                      <div className="flex items-center justify-center gap-2">
                        <FaShoppingBag />
                        Cannot order only add-ons
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <FaShoppingBag />
                        Place Order
                      </div>
                    )}
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-8 space-y-6">
          <ReorderSystem
            userToken={localStorage.getItem('token')}
            onOrderSuccess={(order) => {
              // console.log('Regular reorder successful:', order);
            }}
          />
        </div>

        <EnhancedPaymentModal
          isOpen={showPaymentModal}
          onClose={(wasSuccessful = false) => {
            setShowPaymentModal(false);
            setCurrentCheckoutData(null);
            // Only clear cart if payment was successful
            if (wasSuccessful === true) {
              clearCart();
            }
          }}
          orderData={currentCheckoutData}
          userBalance={userBalance}
          onWalletPayment={processWalletPayment}
          onCardPayment={(orderData) => {
            showInfoNotification('Card payment coming soon!');
          }}
          onFriendPayment={(friendPaymentData) => {
            // showSuccessNotification(`Payment request sent to @${friendPaymentData.friendUsername}!`);
            setShowPaymentModal(false);
            setCurrentCheckoutData(null);
            clearCart();
          }}
          loading={isCheckoutLoading}
        />
        
        {isOrderLoading && <LoadingPage />}
      </div>
    </div>
  );
};

export default CartPage;